import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shield, Star, Upload, Clock } from "lucide-react";

export default function MilitaryVerification() {
  const { user, refresh } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  if (!user) return null;

  const status = user.military_status || "none";
  const isVet = user.is_veteran;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post("/military/id-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Military ID uploaded securely for review");
      await refresh();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="card-metal p-6" data-testid="military-verification-card">
      <h2 className="font-heading font-black text-lg flex items-center gap-2 mb-2">
        <Shield className="w-5 h-5 text-[#9b111e]" /> Military / Veteran Verification
      </h2>

      {isVet ? (
        <p className="text-green-700 font-semibold flex items-center gap-2" data-testid="military-verified">
          <Star className="w-4 h-4 fill-green-600 text-green-600" />
          Verified Veteran ⭐ — as a THANK YOU for your service, your 10% military discount is active on every booking.
        </p>
      ) : status === "pending" ? (
        <p className="text-amber-600 font-semibold flex items-center gap-2" data-testid="military-pending">
          <Clock className="w-4 h-4" />
          Your military ID is under review. We'll verify it and apply your 10% discount shortly.
        </p>
      ) : (
        <div data-testid="military-upload">
          {status === "rejected" && (
            <p className="text-red-600 text-sm mb-2">
              We couldn't verify your last upload. Please upload a clearer photo of your military ID.
            </p>
          )}
          <p className="text-sm text-slate-600 mb-3">
            Active-duty or a veteran? Upload a photo of your military ID to unlock a <b>10% discount</b> on every
            booking and a <b>Verified Veteran ⭐</b> badge — <b>as a THANK YOU for your service</b>.
          </p>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="military-id-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-red-3d" data-testid="military-upload-btn">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Military ID"}
          </button>
        </div>
      )}

      <p className="text-xs text-slate-400 mt-3">
        🔒 Your military ID is <b>viewed only once</b> by our administrator for verification, then{" "}
        <b>permanently deleted</b> — never stored, never shared.
      </p>
    </div>
  );
}
